import type { TFunction } from 'i18next';

import { MFA_FACTOR, MFA_RECOVERY_METHOD, type MfaFactor } from '@repo/common';

export const MFA_PRIMARY_FACTOR_KEY = 'starthub.mfa.primaryFactor';
/** @deprecated Visual-only SMS enrollment; cleared on real enroll/remove. */
export const SMS_ENROLLMENT_KEY = 'starthub.mfa.smsEnrollment';
export const SMS_REPLACE_INTENT_KEY = 'starthub.mfa.replaceSms';
export const PRIMARY_REPLACE_INTENT_KEY = 'starthub.mfa.primaryReplace';

export type MfaPickerMethod = MfaFactor;
export type PrimaryReplaceIntent = { next: MfaPickerMethod; remove: MfaPickerMethod };

/** Legacy visual SMS shape kept only so leftover session keys can be cleared. */
export type VisualSmsEnrollment = {
  dialCode: string;
  enrolledAt: string;
  lastUsedAt: string;
  national: string;
  phone: string;
};

export function clearVisualSmsEnrollment(): void {
  try {
    sessionStorage.removeItem(SMS_ENROLLMENT_KEY);
    if (sessionStorage.getItem(MFA_PRIMARY_FACTOR_KEY) === MFA_FACTOR.Sms) {
      sessionStorage.removeItem(MFA_PRIMARY_FACTOR_KEY);
    }
  } catch {
    return;
  }
}

export function markReplaceSmsIntent(): void {
  try {
    sessionStorage.setItem(SMS_REPLACE_INTENT_KEY, 'true');
  } catch {
    return;
  }
}

export function hasReplaceSmsIntent(): boolean {
  try {
    return sessionStorage.getItem(SMS_REPLACE_INTENT_KEY) === 'true';
  } catch {
    return false;
  }
}

export function consumeReplaceSmsIntent(): boolean {
  if (!hasReplaceSmsIntent()) {
    return false;
  }
  try {
    sessionStorage.removeItem(SMS_REPLACE_INTENT_KEY);
  } catch {
    return true;
  }
  return true;
}

export function isMethodEnrolled(method: MfaPickerMethod, enrolled: readonly string[]): boolean {
  return enrolled.includes(method);
}

function isPickerMethod(value: unknown): value is MfaPickerMethod {
  return (
    value === MFA_FACTOR.Totp || value === MFA_FACTOR.Email || value === MFA_FACTOR.Passkey || value === MFA_FACTOR.Sms
  );
}

export function markPrimaryReplaceIntent(remove: MfaPickerMethod, next: MfaPickerMethod): void {
  try {
    sessionStorage.setItem(PRIMARY_REPLACE_INTENT_KEY, JSON.stringify({ next, remove } satisfies PrimaryReplaceIntent));
  } catch {
    return;
  }
}

export function readPrimaryReplaceIntent(): PrimaryReplaceIntent | undefined {
  try {
    const raw = sessionStorage.getItem(PRIMARY_REPLACE_INTENT_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !('next' in parsed) || !('remove' in parsed)) {
      return undefined;
    }
    if (!(isPickerMethod(parsed.next) && isPickerMethod(parsed.remove))) {
      return undefined;
    }
    return { next: parsed.next, remove: parsed.remove };
  } catch {
    return undefined;
  }
}

export function consumePrimaryReplaceIntent(): PrimaryReplaceIntent | undefined {
  const intent = readPrimaryReplaceIntent();
  clearPrimaryReplaceIntent();
  return intent;
}

export function clearPrimaryReplaceIntent(): void {
  try {
    sessionStorage.removeItem(PRIMARY_REPLACE_INTENT_KEY);
  } catch {
    return;
  }
}

export function passkeyDeviceName(): string {
  const userAgent = globalThis.navigator?.userAgent ?? '';
  if (/Windows/i.test(userAgent)) {
    return 'Windows PC';
  }
  if (/Mac OS|Macintosh/i.test(userAgent)) {
    return 'Mac';
  }
  if (/iPad/i.test(userAgent)) {
    return 'iPad';
  }
  if (/iPhone/i.test(userAgent)) {
    return 'iPhone';
  }
  if (/Android/i.test(userAgent)) {
    return 'Android';
  }
  return 'This device';
}

function fallbackPrimaryFactor(enrolled: readonly string[]): string {
  return enrolled.includes(MFA_FACTOR.Totp) ? MFA_FACTOR.Totp : (enrolled[0] ?? '');
}

export function clearPrimaryFactor(): void {
  try {
    sessionStorage.removeItem(MFA_PRIMARY_FACTOR_KEY);
  } catch {
    return;
  }
}

export function resolvePrimaryFactor(enrolled: readonly string[], accountPrimary?: string): string {
  try {
    if (sessionStorage.getItem(MFA_PRIMARY_FACTOR_KEY)) {
      sessionStorage.removeItem(MFA_PRIMARY_FACTOR_KEY);
    }
  } catch {
    // ignore storage failures
  }
  if (accountPrimary && isPickerMethod(accountPrimary) && enrolled.includes(accountPrimary)) {
    return accountPrimary;
  }
  return fallbackPrimaryFactor(enrolled);
}

export function resolveLoginPrimaryFactor(enrolled: readonly string[], accountPrimary?: string): string {
  if (accountPrimary === MFA_FACTOR.Totp && enrolled.includes(MFA_FACTOR.Totp)) {
    return MFA_FACTOR.Totp;
  }
  if (accountPrimary === MFA_FACTOR.Email && enrolled.includes(MFA_FACTOR.Email)) {
    return MFA_FACTOR.Email;
  }
  if (accountPrimary === MFA_FACTOR.Sms && enrolled.includes(MFA_FACTOR.Sms)) {
    return MFA_FACTOR.Sms;
  }
  if (accountPrimary === MFA_FACTOR.Passkey && enrolled.includes(MFA_FACTOR.Passkey)) {
    return MFA_FACTOR.Passkey;
  }
  return enrolled[0] ?? MFA_RECOVERY_METHOD;
}

export function primaryMethodLabel(
  enrolled: readonly string[],
  t: TFunction<'common'>,
  accountPrimary?: string,
): string {
  const primary = resolvePrimaryFactor(enrolled, accountPrimary);
  if (primary === MFA_FACTOR.Email) {
    return t('security.mfa.emailMethodTitle');
  }
  if (primary === MFA_FACTOR.Sms) {
    return t('security.mfa.smsMethodTitle');
  }
  if (primary === MFA_FACTOR.Passkey) {
    return t('security.mfa.passkeyMethodTitle');
  }
  return t('security.mfa.primaryAuthenticator');
}
